import { Button, Divider, IconButton, MenuItem, Paper, Stack, styled, Typography } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { ExpandLessRounded as ExpandLessRoundedIcon, CloseRounded as CloseRoundedIcon, HelpOutlineRounded as HelpOutlineRoundedIcon } from '@mui/icons-material';
import classNames from "classnames";
import { useAppSelector } from "../../Common/_hooks/useAppSelector";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import { useAppDispatch } from "../../Common/_hooks/useAppDispatch";
import { EditActiveVariant } from "../../UIData/_actions/DesignLabStoreActions";
import SelectV2 from "../../Common/Form/SelectV2";
import type { IProductStore } from "../../AppData/_stores/ProductStore";
import type ProductVariantStore from "../../AppData/_stores/ProductVariantStore";
import type ViewProductStore from "../../AppData/_stores/ViewProductStore";
import type { LabData } from "../../AppData/LabData";
import useIsSaleProduct from "../../Common/_hooks/useIsSaleProduct";
import ProductCatalogPricing from "../../ProductCatalog/Card/ProductCatalogPricing";
import ProductCatalogPrintTypeTag from "../../ProductCatalog/Card/ProductCatalogPrintTypeTag";
import WallpaperRoundedIcon from '@mui/icons-material/WallpaperRounded';
import DesignGuidelinesSummary from "../_components/DesignGuidelinesSummary";
import Tooltip from "../../Common/_components/Tooltip";

const SidebarContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  transition: 'transform 300ms, opacity 300ms',
  top: '30px',

  '@media (max-width: 1510px), (max-height: 1070px)': {
    top: '15px',
  },

  [theme.breakpoints.down('md')]: {
    position: 'fixed',
    bottom: '0px',
    zIndex: 20,
    left: theme.spacing(3),
    right: theme.spacing(3),
    top: 'auto',
    display: 'none',

    '&.has-selected-product': {
      display: 'block',
    }
  },

  '@media (max-width: 600px)': {
    left: theme.spacing(1),
    right: theme.spacing(1),
  },

  '&.hidden': {
    transform: 'translateX(-100px)',
    opacity: '0',
  },

  '&:before': {
    content: '""',
    height: '130px',
    width: '130px',
    borderRadius: '50%',
    border: '20px solid #7FD3F3',
    display: 'block',
    position: 'absolute',
    zIndex: '-1',
    right: '-40px',
    top: '40px',

    [theme.breakpoints.down('md')]: {
      display: 'none',
    }
  },

  '&:after': {
    content: '""',
    height: '150px',
    width: '150px',
    border: '20px solid #FCF57F',
    display: 'block',
    position: 'absolute',
    zIndex: '-1',
    right: '-60px',
    bottom: '120px',
    margin: 'auto',
    transform: 'rotate(-20deg)',

    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  }
}));

const Sidebar = styled(Paper)(({ theme }) => ({
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  background: '#fff',
  marginRight: theme.spacing(4),
  width: '350px',
  flexShrink: '0',
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(5),
  position: 'relative',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',

  '@media (max-width: 1510px), (max-height: 1070px)': {
    marginRight: theme.spacing(2),
  },

  [theme.breakpoints.down('md')]: {
    width: 'auto',
    flex: 1,
    margin: '0',
    paddingTop: '0',
    paddingBottom: 0,
    minHeight: '0',
    transition: 'min-height 250ms',
    overflow: 'hidden',

    '&.opened': {
      minHeight: '75vh',
      maxHeight: '75vh',
      overflow: 'auto',
    }
  },
}));

const SidebarMobileButton = styled(Button)(({ theme }) => ({
  display: 'none',
  marginLeft: '-16px',

  [theme.breakpoints.down('md')]: {
    display: 'flex',
  },
}));

const PrintTypeTagWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  }
}));

const SidebarHeader = styled('div')(({ theme }) => ({
  h2: {
    [theme.breakpoints.down('md')]: {
      fontSize: '32px',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
      fontWeight: 600,
      textTransform: 'none',
      fontFamily: 'Poppins, sans-serif',
    },
  }
}));

const SidebarContent = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 3),

  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(0, 2),
    maxWidth: '600px',
    display: 'none',
  },

  '&.opened': {
    display: 'block',
  }
}));

const SidebarPlaceholder = styled(Stack)(({ theme }) => ({
  color: theme.palette.text.secondary,
  minHeight: '100%',
  padding: theme.spacing(3),

  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const StartButton = styled(Button)(({ theme }) => ({
  textTransform: 'uppercase',
  fontFamily: 'Bebas Neue',
  fontSize: '26px',
  padding: '0 40px',
  height: '55px',
  lineHeight: '58px', //Line height with this font is wack
  borderRadius: '55px',
  flexShrink: '0',

  [theme.breakpoints.down('md')]: {
    height: '40px',
    lineHeight: '40px',
    fontFamily: 'Poppins, sans-serif',
    textTransform: 'none',
    padding: '0 16px',
    fontSize: '14px',
  }
}));

const OptionUl = styled('ul')({
  paddingLeft: '25px',
  margin: '5px 0',
  lineHeight: '30px',

  '&.col2': {
    display: 'flex',
    flexWrap: 'wrap',

    li: {
      flexBasis: '50%',
      paddingRight: '30px',
    }
  }
})

const OptionLi = styled('li')({})

const TemplateSummary = styled(Stack)({
  alignItems: 'center',
  minWidth: 0,
  width: '100%',
});

const TemplateIconCircle = styled('span')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.1)',
  borderRadius: '50%',
  color: theme.palette.secondary.main,
  display: 'inline-flex',
  flexShrink: 0,
  height: 36,
  justifyContent: 'center',
  width: 36,
}));

const TemplateHelpButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  flexShrink: 0,
  marginLeft: 'auto',
  padding: 4,

  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },
}));

type ProductLabData = NonNullable<IProductStore['labData']>;

const getFirstLabData = (productLabData?: ProductLabData): LabData | undefined => {
  if (productLabData === undefined) {
    return undefined;
  }

  if (productLabData.has_subproducts) {
    return Object.values(productLabData.subproducts)[0]?.originalData;
  }

  return productLabData.originalData;
}

interface Props {
  onStart: () => void
  onDeselectSelectedProduct: () => void
  hidden: boolean
  previewVariantByProductId: Record<number, string>
  selectedViewProduct?: ViewProductStore
  onPreviewVariantChange: (productId: number, variant: string) => void
}

export default function ProductSidebar(props: Props) {
  const dispatch = useAppDispatch()
  const isSaleProduct = useIsSaleProduct();
  const { onPreviewVariantChange, previewVariantByProductId } = props;

  const [t] = useTranslation('design-lab');

  const activeProductId = useAppSelector(state => state.get('UIData').get('designLab').get('activeProductId'));
  const activeVariant = useAppSelector(state => state.get('UIData').get('designLab').get('activeVariant'));
  const selectedProductId = props.selectedViewProduct?.get('id_product') ?? null;
  const viewProducts = useAppSelector(state => state.get('appData').get('viewProducts'));
  const productData = useAppSelector(state => (
    selectedProductId !== null
      ? state.get('appData').get('products').get(String(selectedProductId))
      : undefined
  ));
  const variantTypes = useAppSelector(state => state.get('appData').get('productVariantTypes'));

  const [sidebarOpened, setSidebarOpened] = useState(false);
  const selectedViewProductName = props.selectedViewProduct?.get('name') ?? '';
  const selectedViewProductDescription = props.selectedViewProduct?.get('description') ?? '';
  const productName = selectedViewProductName !== '' ? selectedViewProductName : productData?.get('name');
  const productDescription = selectedViewProductDescription !== ''
    ? selectedViewProductDescription
    : productData?.get('shop_description');
  const isOnSale = isSaleProduct(selectedProductId);
  const templateViewProducts = useMemo(() => {
    if (selectedProductId === null) {
      return [];
    }

    return viewProducts
      .valueSeq()
      .filter(viewProduct => viewProduct.get('id_product') === selectedProductId)
      .sortBy(viewProduct => viewProduct.get('name'))
      .toArray();
  }, [selectedProductId, viewProducts])
  const selectedVariant = useMemo(() => {
    if (!productData) {
      return activeVariant;
    }

    const firstVariant = productData.get('variants').first()?.get('slug') ?? activeVariant;

    if (selectedProductId !== null && selectedProductId !== activeProductId) {
      const previewVariant = previewVariantByProductId[selectedProductId];
      const variant = productData.get('variants').find(variant => variant.get('slug') === previewVariant);

      return variant?.get('slug') ?? firstVariant;
    }

    const variant = productData.get('variants').find(variant => variant.get('slug') === activeVariant);
    return variant?.get('slug') ?? firstVariant;
  }, [activeProductId, activeVariant, previewVariantByProductId, productData, selectedProductId])
  const designGuidelinesLabData = useMemo(() => getFirstLabData(productData?.get('labData')), [productData])
  const firstVariantType = useMemo(() => {
    const firstVariant = productData?.get('variants').first();

    if (firstVariant === undefined) {
      return undefined;
    }

    return variantTypes.get(String(firstVariant.get('id_type')));
  }, [productData, variantTypes])

  const onSidebarToggle = useCallback(() => {
    setSidebarOpened(value => !value)
  }, [])

  const listSizes = useMemo(() => {
    //If we have a size variant, don't display the sizes list as the variant selector already shows them
    if (!productData) {
      return undefined;
    }

    if (firstVariantType?.get('slug') === 'size') return undefined;

    const options = productData.get('availableOptionTypes').get('1')?.get('options');
    if (!options) return undefined;

    const list: string[] = [];
    options.forEach(option => {
      if (list.includes(option.get('name'))) return;
      list.push(option.get('name'));
    });
    return list;
  }, [firstVariantType, productData]);

  const onChangeVariant = useCallback((e: SelectChangeEvent) => {
    const variant = String(e.target.value);

    if (selectedProductId !== null && selectedProductId !== activeProductId) {
      onPreviewVariantChange(selectedProductId, variant);
      return;
    }

    dispatch(EditActiveVariant(variant));
  }, [activeProductId, dispatch, onPreviewVariantChange, selectedProductId])

  const renderVariant = useCallback((variant: ProductVariantStore) => {
    return <MenuItem key={variant.get('id')} value={variant.get('slug')}>{variant.get('name')}</MenuItem>
  }, [])

  const renderTemplateViewProduct = useCallback((viewProduct: ViewProductStore) => {
    return <Typography
      component="li"
      key={viewProduct.get('id')}
      variant="caption"
      sx={{ color: 'inherit' }}
    >
      {viewProduct.get('name')}
    </Typography>
  }, [])

  const renderOption = useCallback((option: string) => {
    return <OptionLi key={option}>{option}</OptionLi>
  }, [])

  return <SidebarContainer className={classNames({
    hidden: props.hidden,
    'has-selected-product': productData !== undefined
  })}>
    <Sidebar
      elevation={16}
      className={(props.hidden ? 'hidden' : '') + (sidebarOpened ? ' opened' : '')}
    >
      {productData ? <>

        <Stack
          spacing={1}
        >
          <Stack>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                pl: {
                  xs: 2,
                  sm: 3,
                },
                pr: {
                  xs: 5,
                  md: 3,
                },
                pt: {
                  xs: 2,
                  sm: 2,
                  md: 0,
                },
                pb: {
                  xs: 0,
                  sm: 1,
                  md: 0,
                }
              }}
            >
              <SidebarHeader>
                {props.selectedViewProduct !== undefined ? <PrintTypeTagWrapper>
                  <ProductCatalogPrintTypeTag product={props.selectedViewProduct} />
                </PrintTypeTagWrapper> : null}
                <Typography variant="h2">{productName}</Typography>
                <SidebarMobileButton
                  color="secondary"
                  onClick={onSidebarToggle}
                  endIcon={<ExpandLessRoundedIcon
                    fontSize="small"
                    sx={{
                      transform: sidebarOpened ? 'rotate(0deg)' : 'rotate(180deg)',
                      transition: 'transform 250ms',
                    }}
                  />}
                >{t('View details')}</SidebarMobileButton>
              </SidebarHeader>
              <StartButton
                color="accent"
                variant="contained"
                disableElevation
                className={sidebarOpened ? 'opened' : ''}
                onClick={props.onStart}
                sx={{
                  display: {
                    xs: 'block',
                    md: 'none',
                  }
                }}
              >{t('Start creating')}</StartButton>
            </Stack>
          </Stack>
          <SidebarContent className={sidebarOpened ? 'opened' : ''}>
            <Stack spacing={2} divider={<Divider />}>
              <Stack spacing={2} sx={{pb: 2}}>
                <p
                  style={{
                    fontSize: '15px',
                    lineHeight: '1.5em',
                  }}
                >
                  {productDescription}
                </p>

                {props.selectedViewProduct !== undefined ? <ProductCatalogPricing
                  product={props.selectedViewProduct}
                  isOnSale={isOnSale}
                /> : null}

                {productData.get('variants').count() > 0 ? <SelectV2
                  label={firstVariantType?.get('name') ?? t('Variant')}
                  value={selectedVariant}
                  onChange={onChangeVariant}
                  InputLabelProps={{
                    sx: {
                      fontWeight: '600'
                    }
                  }}
                >
                  {productData.get('variants').sortBy(variant => variant.get('theorder')).valueSeq().map(renderVariant)}
                </SelectV2> : null}

                <StartButton
                  color="accent"
                  variant="contained"
                  disableElevation
                  className={sidebarOpened ? 'opened' : ''}
                  onClick={props.onStart}
                  sx={{
                    alignSelf: 'flex-start',
                    display: {
                      xs: 'none',
                      md: 'block',
                    }
                  }}
                >{t('Start creating')}</StartButton>
              </Stack>

              {productData.get('labData') !== undefined ? <TemplateSummary direction="row" spacing={1}>
                <TemplateIconCircle>
                  <WallpaperRoundedIcon fontSize="medium" />
                </TemplateIconCircle>
                <Stack
                  sx={{
                    flex: '1 1 auto',
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      textTransform: 'uppercase',
                      lineHeight: 1,
                      mt: 0.5,
                    }}
                  >
                    {t('Template')}
                  </Typography>
                  <Typography variant="body2">
                    {productData.get('name')}
                  </Typography>
                </Stack>
                <Tooltip
                  arrow
                  enterTouchDelay={0}
                  leaveTouchDelay={5000}
                  placement="top"
                  variant="multiline"
                  title={<Stack spacing={1}>
                    <Typography variant="body2" sx={{ color: 'inherit' }}>
                      {t('All products that use this template will be available as options when you order your design.')}
                    </Typography>
                    {templateViewProducts.length > 0 ? <Stack
                      component="ul"
                      spacing={0.25}
                      sx={{
                        m: 0,
                        pl: 2,
                      }}
                    >
                      {templateViewProducts.map(renderTemplateViewProduct)}
                    </Stack> : null}
                  </Stack>}
                >
                  <TemplateHelpButton
                    aria-label={t('About product templates')}
                    size="small"
                  >
                    <HelpOutlineRoundedIcon />
                  </TemplateHelpButton>
                </Tooltip>
              </TemplateSummary> : null}

              {designGuidelinesLabData !== undefined ? <DesignGuidelinesSummary
                labData={designGuidelinesLabData}
                selectedVariant={selectedVariant}
                productName={productData.get('name')}
              /> : null}

              {listSizes !== undefined ? <Stack>
                <div style={{ marginBottom: '20px' }}>
                  <Typography
                    variant="h4"
                  >{t('Sizes available')}</Typography>
                  <OptionUl className={listSizes.length > 10 ? 'col2' : ''}>
                    {listSizes.map(renderOption)}
                  </OptionUl>
                </div>

              </Stack> : null}
            </Stack>
          </SidebarContent>
        </Stack>
      </> : <SidebarPlaceholder justifyContent="center" spacing={1.5}>
        <Typography variant="h3" color="text.primary">{t('Choose a product')}</Typography>
        <Typography variant="body1">
          {t('Details and guidelines will be shown here to help you create your design.')}
        </Typography>
      </SidebarPlaceholder>}

    </Sidebar>

    <IconButton
      color="secondary"
      onClick={props.onDeselectSelectedProduct}
      sx={{
        position: 'absolute',
        right: 0,
        top: 0,
        display: {
          xs: 'inline-flex',
          md: 'none',
        }
      }}
    >
      <CloseRoundedIcon fontSize="large" color="inherit" />
    </IconButton>
  </SidebarContainer>
}
